import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "../api";

// Global coordination for pipeline-affecting actions (camera add/remove,
// simulation-store changes, preview settings): the UI shows an "applying"
// state and every control that could collide with the change is disabled until
// it settles, even across pages.
//
// Changing which store you are LOOKING at is not one of these actions any
// more. Every store's cameras ingest continuously, so scope is a per-user view
// setting (see ScopeContext) that costs a refetch and nothing else — no
// restart, no waiting, and no effect on anybody else's session.

type Phase = "idle" | "applying" | "verifying";

type PipelineControlValue = {
  /** True while a pipeline-affecting action is applying or the pipeline is
   *  still coming back online. Controls should disable on this. */
  busy: boolean;
  phase: Phase;
  /** Human-readable label for the current action (banner text). */
  label: string;
  /** Transient confirmation of a completed action, shown in the banner. */
  storeChangeNote: string | null;
  setStoreChangeNote: (note: string | null) => void;
  clearStoreChangeNote: () => void;
  /** Wrap a pipeline-affecting mutation so the global busy state + banner track
   *  it. With `waitReady`, stays busy until the pipeline reports ready. */
  runControl: <T>(
    label: string,
    fn: () => Promise<T>,
    opts?: { waitReady?: boolean },
  ) => Promise<T>;
};

const Ctx = createContext<PipelineControlValue | null>(null);

export function usePipelineControl(): PipelineControlValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("PipelineControlProvider missing");
  return v;
}

// A fleet with cameras returns to "streaming"; an empty one settles at
// "no_cameras". Both mean the change finished — stop waiting.
const READY_STATES = new Set(["streaming", "no_cameras"]);
const VERIFY_TIMEOUT_MS = 45_000;
const VERIFY_INTERVAL_MS = 2_000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function waitPipelineReady(): Promise<void> {
  const deadline = Date.now() + VERIFY_TIMEOUT_MS;
  // Give the just-restarted containers a beat before the first probe (deepstream
  // needs a moment to link RTSP sources and start decoding).
  await sleep(VERIFY_INTERVAL_MS);
  while (Date.now() < deadline) {
    try {
      const a = await api.pipeline.activity();
      if (READY_STATES.has(a.state)) return;
    } catch {
      // 5xx / network blips are expected while the api or pipeline restarts.
    }
    await sleep(VERIFY_INTERVAL_MS);
  }
}

export function PipelineControlProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [label, setLabel] = useState("");
  const pending = useRef(0);
  const [storeChangeNote, setStoreChangeNote] = useState<string | null>(null);

  const runControl = useCallback(
    async <T,>(
      lbl: string,
      fn: () => Promise<T>,
      opts?: { waitReady?: boolean },
    ): Promise<T> => {
      pending.current += 1;
      setLabel(lbl);
      setPhase("applying");
      try {
        return await fn();
      } finally {
        pending.current -= 1;
        if (pending.current <= 0) {
          pending.current = 0;
          if (opts?.waitReady) {
            setPhase("verifying");
            setLabel("Reloading the pipeline — waiting for live capture to come back online…");
            await waitPipelineReady();
          }
          setPhase("idle");
          setLabel("");
        }
      }
    },
    [],
  );

  const value = useMemo<PipelineControlValue>(
    () => ({
      busy: phase !== "idle",
      phase,
      label,
      storeChangeNote,
      setStoreChangeNote,
      clearStoreChangeNote: () => setStoreChangeNote(null),
      runControl,
    }),
    [phase, label, storeChangeNote, runControl],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
